import * as React from 'react';
import { connect } from 'react-redux';
import { select } from '@rematch/select';
import { Formik } from 'formik';
import { withTranslation } from 'react-i18next';

import { InterestsField } from '../form/smart-fields';
import { Box } from '../box';
import { Button } from '../button';
import { ThemeConsumer } from '../theme';
import { TextField, SelectField, CropperEditorField } from '../form';
import { validator } from '../../utils/validator';
import { USstates } from '../../utils/us-states';
import { countries } from '../../utils/countries';
import { timezones } from 'common/utils';
import { PhoneNumberField } from '../form/PhoneNumberField';
import { BirthDateSelect } from '../form/DateField/BirthDateSelect';

export const DEFAULT_VALUES = {
  first_name: '',
  middle_name: '',
  last_name: '',
  email: '',
  phone: '',
  company: '',
  biography: '',
  interests: [],
  profession: '',
  gender: '',
  education_level: '',
  birth_day: '',
  birth_month: '',
  birth_year: '',
  zip: '',
  street: '',
  apt: '',
  country: '',
  state: '',
  city: '',
  profile_photo: {},
  time_zone: '',
};

export class Renderer extends React.PureComponent {
  static defaultProps = {
    error: '',
  };

  componentDidMount() {
    this.props.fetchProfessions();
    this.props.fetchGenders();
    this.props.fetchEducationLevels();
  }

  getList = (key) => {
    const list = this.props[key];

    return list.map((item) => ({
      label: item.name,
      value: item.id,
    }));
  };

  get professionsList() {
    return this.getList('professions');
  }

  get genderList() {
    return this.getList('genders');
  }

  get educationLevelList() {
    return this.getList('educationLevels');
  }

  render() {
    const { initialValues, onSubmit } = this.props;

    return (
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={(values) => {
          onSubmit(values);
        }}
        validate={(values) => {
          if (!values.phone) {
            values.phone = '';
          }

          if (values.country !== 'US') {
            values.state = '';
          }

          return validator.validateAll(
            values,
            {
              first_name: 'required',
              last_name: 'required',
              email: 'required|email',
              phone: 'required|phone_number',
              birth_day: 'required',
              birth_month: 'required',
              birth_year: 'required',
              interests: 'max:3',
            },
            {
              'max.interests': this.props.t('Please, select maximum 3 interests'),
            },
          );
        }}
      >
        {(formik) => (
          <ThemeConsumer>
            {(theme) => (
              <div style={{ display: 'flex', flexDirection: 'column', padding: '0 33px' }}>
                <Box flexDirection="row" style={{ marginBottom: 15 }}>
                  <CropperEditorField name="profile_photo" id="my-photo" />
                </Box>
                <TextField
                  name="first_name"
                  placeholder={this.props.t('First name')}
                  label={this.props.t('First name')}
                  id={'first-name'}
                />
                <TextField
                  name="middle_name"
                  placeholder={this.props.t('Middle name')}
                  label={this.props.t('Middle name')}
                  id={'middle-name'}
                />
                <TextField
                  name="last_name"
                  placeholder={this.props.t('Last name')}
                  label={this.props.t('Last name')}
                  id={'last-name'}
                />
                <TextField name="email" placeholder={this.props.t('Email')} label={this.props.t('Email')} id="email" />
                <PhoneNumberField
                  name="phone"
                  required="true"
                  label={this.props.t('Phone Number')}
                  placeholder={this.props.t('Phone Number')}
                  id="phone"
                />
                <SelectField
                  name="time_zone"
                  getOptionLabel={(option) => option.label}
                  getOptionValue={(option) => option.value}
                  label={this.props.t('My timezone')}
                  options={timezones}
                  placeholder={this.props.t('My timezone')}
                  id={'select-timezone'}
                />
                <SelectField
                  name="gender"
                  getOptionLabel={(option) => option.label}
                  getOptionValue={(option) => option.value}
                  label={this.props.t('Gender')}
                  options={this.genderList}
                  placeholder={this.props.t('Please, select gender')}
                  id={'select-gender'}
                />
                <div className={'select-field select-field__label visible'}>{this.props.t('Date of birth')}</div>
                <BirthDateSelect form={formik} />
                <SelectField
                  name="education_level"
                  getOptionLabel={(option) => option.label}
                  getOptionValue={(option) => option.value}
                  label={this.props.t('Education level')}
                  options={this.educationLevelList}
                  placeholder={this.props.t('Please, select education level')}
                  id={'select-education'}
                />
                <TextField
                  name="biography"
                  placeholder={this.props.t('Biography')}
                  label={this.props.t('Biography')}
                  id={'biography'}
                />
                <TextField
                  name="street"
                  placeholder={this.props.t('Street')}
                  label={this.props.t('Street')}
                  id={'street'}
                />
                <TextField
                  name="apt"
                  placeholder={this.props.t('Apartment')}
                  label={this.props.t('Apartment')}
                  id={'apartment'}
                />
                <SelectField
                  name="country"
                  getOptionLabel={(option) => option.label}
                  getOptionValue={(option) => option.value}
                  label={this.props.t('Country')}
                  options={countries.map((option) => ({
                    label: option.name,
                    value: option.code,
                  }))}
                  placeholder={this.props.t('Country')}
                  id={'select-country'}
                />
                {formik.values['country'] === 'US' && (
                  <SelectField
                    name="state"
                    getOptionLabel={(option) => option.label}
                    getOptionValue={(option) => option.value}
                    label={this.props.t('State')}
                    options={USstates.map((option) => ({
                      label: option.name,
                      value: option.code,
                    }))}
                    placeholder={this.props.t('State')}
                    id={'select-state'}
                  />
                )}
                <TextField name="city" placeholder={this.props.t('City')} label={this.props.t('City')} id={'city'} />
                <TextField name="zip" placeholder={this.props.t('ZIP')} label={this.props.t('ZIP')} id={'zip'} />
                <TextField
                  name="company"
                  placeholder={this.props.t('Company name/school')}
                  label={this.props.t('Company name/school')}
                  id={'company-name'}
                />
                <SelectField
                  name="profession"
                  getOptionLabel={(option) => option.label}
                  getOptionValue={(option) => option.value}
                  label={this.props.t('Profession')}
                  options={this.professionsList}
                  placeholder={this.props.t('Please, select profession')}
                  id={'select-profession'}
                />
                <InterestsField
                  name="interests"
                  placeholder={this.props.t('Choose...')}
                  label={this.props.t('Interests (more options to choose from)')}
                  maxCount={3}
                />
                <div style={{ margin: '30px 0', width: 150 }}>
                  <Button
                    block
                    size="lg"
                    title={this.props.t('Submit')}
                    type="submit"
                    color={theme.color.brand}
                    onClick={formik.submitForm}
                    textColor="white"
                    id={'submit-button'}
                  />
                </div>
              </div>
            )}
          </ThemeConsumer>
        )}
      </Formik>
    );
  }
}

const mapDispatch = (dispatch) => ({
  fetchProfessions: () => dispatch.professions.fetchAll(),
  fetchGenders: () => dispatch.genders.fetchAll(),
  fetchEducationLevels: () => dispatch.educationLevels.fetchAll(),
});

const mapState = (state) => ({
  professions: select.professions.get(state),
  genders: select.genders.get(state),
  educationLevels: select.educationLevels.get(state),
});

export const ProfileForm = withTranslation()(connect(mapState, mapDispatch)(Renderer));
